/**
 * TooltipCapsuleBar
 *
 * A utility that generates inline HTML capsule bars for use inside ECharts
 * tooltip formatters. It mirrors the visual language of the CapsuleBar React
 * component but outputs a raw HTML string so it can be embedded in tooltip
 * `formatter` return values.
 *
 * FEATURES:
 * 1. Positive-only capsule — blue fill grows left-to-right; KPI marker sits
 *    on top at the KPI position.
 * 2. Mixed (negative) capsule — a gap splits the bar in the centre; negative
 *    value extends left, positive extends right, KPI marker repositioned
 *    accordingly.
 * 3. Fully configurable colors for the bar fill, KPI marker, background
 *    track, and the centre gap.
 *
 * USAGE (inside an ECharts tooltip formatter):
 *   import { buildTooltipCapsuleBar } from '@sg/components/CapsuleBar/TooltipCapsuleBar';
 *
 *   const capsuleHtml = buildTooltipCapsuleBar({
 *     value: 28.8,
 *     kpiValue: 32.8,
 *     barWidth: 100,    // px, default 100
 *     barHeight: 6,     // px, default 6
 *   });
 *
 *   // Negative value example:
 *   const negativeCapsuleHtml = buildTooltipCapsuleBar({
 *     value: -1.4,
 *     kpiValue: 1.8,
 *   });
 */

/**
 * Whitespace coefficient — multiplied against the max reference value so that
 * the longest bar / KPI marker never touches the container edge.
 * Increase → more breathing room; decrease → tighter fit.
 */
const WHITESPACE_COEFFICIENT = 1.1;

export interface ITooltipCapsuleBarOptions {
  /** The actual/primary value (can be negative). */
  value: number;
  /** The KPI / target value (always treated as the reference point). */
  kpiValue: number;
  /** Total width of the bar container in px. @default 100 */
  barWidth?: number;
  /** Height of the bar in px. @default 6 */
  barHeight?: number;
  /** Fill color of the value bar. @default '#5EC8F1' */
  barColor?: string;
  /** Color of the KPI marker. @default '#FC8B00' */
  kpiMarkerColor?: string;
  /** Background track color. @default 'rgba(242, 243, 245, 0.16)' */
  trackColor?: string;
  /** Color of the gap between negative and positive sections. @default '#43444D' */
  gapColor?: string;
  /** Width of the gap between negative/positive sections in px. @default 3 */
  gapWidth?: number;
  /** Width of the KPI marker in px. @default 5 */
  kpiMarkerWidth?: number;
  /** Border-radius of the bar in px. @default 3 */
  borderRadius?: number;
  /**
   * Capsule layout type.
   * - 'positive': standard left-to-right fill (default when value >= 0)
   * - 'mixed': split layout with centre gap for negative/positive halves
   * When omitted, the type is auto-detected from the value sign.
   * Useful when all capsules in a group should share the same layout style. 
   */
  type?: 'positive' | 'mixed';
}

export interface ITooltipCapsulePlaceholderOptions {
  /** Total width of the placeholder bar in px. @default 100 */
  barWidth?: number;
  /** Height of the placeholder bar in px. @default 6 */
  barHeight?: number;
  /** Background track color. @default 'rgba(242, 243, 245, 0.16)' */
  trackColor?: string;
  /** Color of the gap between negative/positive sections. @default '#43444D' */
  gapColor?: string;
  /** Width of the gap between negative/positive sections in px. @default 3 */
  gapWidth?: number;
  /** Border-radius of the placeholder in px. @default 3 */
  borderRadius?: number;
  /** Placeholder layout type. @default 'positive' */
  type?: 'positive' | 'mixed';
}

/**
 * Build an inline-HTML capsule bar string for use in ECharts tooltip
 * formatters.
 *
 * The bar is a small progress-bar-like element:
 * ┌──────────────────────────────────┐
 * │ ████████████████░░░░│KPI░░░░░░░ │  (positive only)
 * └──────────────────────────────────┘
 *
 * When value < 0:
 * ┌──────────────────────────────────┐
 * │ ░░░░░░██◄ GAP ►██████│KPI░░░░░ │  (mixed)
 * └──────────────────────────────────┘
 */
export function buildTooltipCapsuleBar(options: ITooltipCapsuleBarOptions): string {
  const {
    value,
    kpiValue,
    barWidth = 100,
    barHeight = 6,
    barColor = '#5EC8F1',
    kpiMarkerColor = '#FC8B00',
    trackColor = 'rgba(242, 243, 245, 0.16)',
    gapColor = '#43444D',
    gapWidth = 3,
    kpiMarkerWidth = 5,
    borderRadius = 3,
    type,
  } = options;

  // Auto-detect type from value sign when not explicitly provided
  const resolvedType = type ?? (value < 0 ? 'mixed' : 'positive');

  if (resolvedType === 'mixed') {
    return buildMixedBar({
      value,
      kpiValue,
      barWidth,
      barHeight,
      barColor,
      kpiMarkerColor,
      trackColor,
      gapColor,
      gapWidth,
      kpiMarkerWidth,
      borderRadius,
    });
  }

  return buildPositiveBar({
    value,
    kpiValue,
    barWidth,
    barHeight,
    barColor,
    kpiMarkerColor,
    trackColor,
    kpiMarkerWidth,
    borderRadius,
  });
}

/**
 * Build a lightweight placeholder capsule track.
 * Used when KPI/value is missing but layout should stay aligned.
 */
export function buildTooltipCapsulePlaceholder(
  options: ITooltipCapsulePlaceholderOptions = {},
): string {
  const {
    barWidth = 100,
    barHeight = 6,
    trackColor = 'rgba(242, 243, 245, 0.16)',
    gapColor = '#43444D',
    gapWidth = 3,
    borderRadius = 3,
    type = 'positive',
  } = options;

  if (type === 'mixed') {
    const halfWidth = (barWidth - gapWidth) / 2;
    return `<div style="position:relative; width:${barWidth}px; height:${barHeight}px; overflow:visible; display:flex;">` +
      `<div style="position:relative; width:${halfWidth}px; height:100%; background:${trackColor}; border-radius:${borderRadius}px 0 0 ${borderRadius}px; overflow:hidden;"></div>` +
      `<div style="width:${gapWidth}px; height:100%; background:${gapColor};"></div>` +
      `<div style="position:relative; width:${halfWidth}px; height:100%; background:${trackColor}; border-radius:0 ${borderRadius}px ${borderRadius}px 0; overflow:hidden;"></div>` +
      `</div>`;
  }

  return `<div style="width:${barWidth}px; height:${barHeight}px; background:${trackColor}; border-radius:${borderRadius}px;"></div>`;
}

/* ------------------------------------------------------------------ */
/*  Positive-only bar                                                  */
/* ------------------------------------------------------------------ */
function buildPositiveBar(opts: {
  value: number;
  kpiValue: number;
  barWidth: number;
  barHeight: number;
  barColor: string;
  kpiMarkerColor: string;
  trackColor: string;
  kpiMarkerWidth: number;
  borderRadius: number;
}): string {
  const {
    value,
    kpiValue,
    barWidth,
    barHeight,
    barColor,
    kpiMarkerColor,
    trackColor,
    kpiMarkerWidth,
    borderRadius,
  } = opts;

  // The reference length is max(|value|, |kpiValue|) * coefficient (留白)
  const maxRef = Math.max(Math.abs(value), Math.abs(kpiValue)) * WHITESPACE_COEFFICIENT;
  const fillPercent = maxRef ? (Math.abs(value) / maxRef) * 100 : 0;
  const kpiPercent = maxRef ? (Math.abs(kpiValue) / maxRef) * 100 : 0;

  // Clamp to [0, 100]
  const clampedFill = Math.min(Math.max(fillPercent, 0), 100);
  const clampedKpi = Math.min(Math.max(kpiPercent, 0), 100);

  // KPI marker left position (center the marker on the kpi point)
  const kpiLeftRaw = (clampedKpi / 100) * barWidth - kpiMarkerWidth / 2;
  const kpiLeft = Math.max(0, Math.min(barWidth - kpiMarkerWidth, kpiLeftRaw));

  return `<div style="position:relative; width:${barWidth}px; height:${barHeight}px; background:${trackColor}; border-radius:${borderRadius}px; overflow:visible;">` +
    // Value fill bar
    `<div style="position:absolute; left:0; top:0; width:${clampedFill}%; height:100%; background:${barColor}; border-radius:${borderRadius}px 0 0 ${borderRadius}px;"></div>` +
    // KPI marker (with #43444D stroke border per Figma)
    `<div style="position:absolute; left:${kpiLeft}px; top:0; width:${kpiMarkerWidth}px; height:100%; background:#43444D; border-radius:1px; z-index:2; display:flex; align-items:center; justify-content:center;">` +
    `<div style="width:${kpiMarkerWidth - 2}px; height:100%; background:${kpiMarkerColor}; border-radius:1px;"></div>` +
    `</div>` +
    `</div>`;
}

/* ------------------------------------------------------------------ */
/*  Mixed (negative value) bar                                         */
/* ------------------------------------------------------------------ */
function buildMixedBar(opts: {
  value: number;
  kpiValue: number;
  barWidth: number;
  barHeight: number;
  barColor: string;
  kpiMarkerColor: string;
  trackColor: string;
  gapColor: string;
  gapWidth: number;
  kpiMarkerWidth: number;
  borderRadius: number;
}): string {
  const {
    value,
    kpiValue,
    barWidth,
    barHeight,
    barColor,
    kpiMarkerColor,
    trackColor,
    gapColor,
    gapWidth,
    kpiMarkerWidth,
    borderRadius,
  } = opts;

  // For mixed mode the centre gap splits the bar into left (negative) and
  // right (positive) halves.
  //
  // Layout:   [  left-track  |GAP|  right-track  ]
  //
  // The negative value bar fills the left half from right-to-left.
  // The KPI marker sits in the right half.
  //
  // Available space for each half = (barWidth - gapWidth) / 2

  const halfWidth = (barWidth - gapWidth) / 2;

  // Reference: the max absolute value among value and kpiValue * coefficient (留白)
  const maxRef = Math.max(Math.abs(value), Math.abs(kpiValue)) * WHITESPACE_COEFFICIENT;

  const isActuallyNegative = value < 0;

  // Fill percentage based on absolute value
  const fillPercent = maxRef ? (Math.abs(value) / maxRef) * 100 : 0;
  const clampedFill = Math.min(Math.max(fillPercent, 0), 100);
  const fillPx = (clampedFill / 100) * halfWidth;

  // KPI marker position depends on KPI sign:
  // Positive KPI → right half, Negative KPI → left half
  const kpiPercent = maxRef ? (Math.abs(kpiValue) / maxRef) * 100 : 0;
  const clampedKpi = Math.min(Math.max(kpiPercent, 0), 100);
  const kpiOffsetPx = (clampedKpi / 100) * halfWidth;

  let kpiAbsLeft: number;
  if (kpiValue >= 0) {
    // Positive KPI: position in right half (from centre gap rightward)
    kpiAbsLeft = halfWidth + gapWidth + kpiOffsetPx - kpiMarkerWidth / 2;
  } else {
    // Negative KPI: position in left half (from centre gap leftward)
    kpiAbsLeft = halfWidth - kpiOffsetPx - kpiMarkerWidth / 2;
  }

  kpiAbsLeft = Math.max(0, Math.min(barWidth - kpiMarkerWidth, kpiAbsLeft));

  // When value is negative: fill goes in LEFT half (grows from centre towards left)
  // When value is positive (forceMixed): fill goes in RIGHT half (grows from centre towards right)
  const leftFillHtml = isActuallyNegative
    ? `<div style="position:absolute; right:0; top:0; width:${fillPx}px; height:100%; background:${barColor}; border-radius:${borderRadius}px 0 0 ${borderRadius}px;"></div>`
    : '';
  const rightFillHtml = !isActuallyNegative
    ? `<div style="position:absolute; left:0; top:0; width:${fillPx}px; height:100%; background:${barColor}; border-radius:0 ${borderRadius}px ${borderRadius}px 0;"></div>`
    : '';

  return (
    `<div style="position:relative; width:${barWidth}px; height:${barHeight}px; overflow:visible; display:flex;">` +
    // Left half (negative side) — track
    `<div style="position:relative; width:${halfWidth}px; height:100%; background:${trackColor}; border-radius:${borderRadius}px 0 0 ${borderRadius}px; overflow:hidden;">` +
    leftFillHtml +
    `</div>` +
    // Centre gap
    `<div style="width:${gapWidth}px; height:100%; background:${gapColor};"></div>` +
    // Right half (positive side) — track
    `<div style="position:relative; width:${halfWidth}px; height:100%; background:${trackColor}; border-radius:0 ${borderRadius}px ${borderRadius}px 0; overflow:hidden;">` +
    rightFillHtml +
    `</div>` +
    // KPI marker (with #43444D stroke border per Figma)
    `<div style="position:absolute; left:${kpiAbsLeft}px; top:0; width:${kpiMarkerWidth}px; height:100%; background:#43444D; border-radius:1px; z-index:2; display:flex; align-items:center; justify-content:center;">` +
    `<div style="width:${kpiMarkerWidth - 2}px; height:100%; background:${kpiMarkerColor}; border-radius:1px;"></div>` +
    `</div>` +
    `</div>`
  );
}

export default buildTooltipCapsuleBar;
